import { useRef } from 'react';
import { BearerToken } from '@marageti/z4-sdk/lib/auth';
import { Traveler } from '@marageti/z4-sdk/lib/people';
import {
  GoogleLoginRequest,
  LoginRequest,
  VerificationLoginRequest,
} from '@marageti/z4-sdk/lib/auth/Token';
import {
  hasAuthorizedRole,
  LocalStorageService,
  useApiClient,
  useQueryParams,
} from '@marageti/z4-lib';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { authorizedRoles, getMatchingUser } from '../../utils/auth';
import {
  emitLogin,
  emitLoginEmailOtp,
  emitLoginSmsOtp,
  emitLoginSSO,
} from '../../utils/gtm/events';
import { buildLoginPath } from './helpers';

const LocalStorage = new LocalStorageService();

export type LoginAuthRequest =
  | LoginRequest
  | GoogleLoginRequest
  | VerificationLoginRequest;

const isGoogleLogin = (request: LoginAuthRequest): request is GoogleLoginRequest => (
  'googleToken' in request
);

const isVerificationLogin = (request: LoginAuthRequest): request is VerificationLoginRequest => (
  'channel' in request && 'code' in request
);

const emitLoginForRequest = (loginRequest: LoginAuthRequest): void => {
  if (isGoogleLogin(loginRequest)) {
    emitLoginSSO();
    return;
  }
  if (isVerificationLogin(loginRequest)) {
    if (loginRequest.channel === 'sms') {
      emitLoginSmsOtp();
      return;
    }
    emitLoginEmailOtp();
    return;
  }
  emitLogin();
};

/**
 * Shared login session pipeline for password, Google SSO, and verification OTP.
 *
 * When entering from a trip request (`?tripRequestId=`), the session traveler is
 * stored but `isAuthenticated` stays false until confirm-phone finishes — otherwise
 * ProtectedRoutes would skip that step.
 */
const useLoginAuth = () => {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { tripRequestId } = useQueryParams();
  const {
    clearAuthenticatedTraveler,
    setIsAuthenticated,
    setAuthenticatedTraveler,
  } = useAuthStore();
  const loginInFlightRef = useRef<Promise<void> | null>(null);

  const requiresPhoneConfirmation = !!tripRequestId;

  const login = (loginRequest: LoginAuthRequest): Promise<void> => {
    // Coalesce duplicate submits (OTP onComplete + Sign in) onto one attempt.
    if (loginInFlightRef.current) {
      return loginInFlightRef.current;
    }

    const attempt = (async () => {
      try {
        const data: BearerToken = await apiClient.authClient.login(loginRequest);
        apiClient.instance.setAuthToken(data.accessToken);
        const token = await apiClient.authClient.getToken(data.accessToken);

        LocalStorage.addToLocalStorage('accessToken', data.accessToken, (90 * 24 * 60 * 60 * 1000));
        LocalStorage.addToLocalStorage('email', token.email);
        if (token.superEmail) {
          LocalStorage.addToLocalStorage('superEmail', token.superEmail);
        }

        const traveler: Traveler = await apiClient.peopleClient.getTraveler(token.email);
        const matchingUser = getMatchingUser(traveler.email, traveler.userInfo);
        if (!matchingUser || !hasAuthorizedRole(matchingUser.roles, authorizedRoles, true)) {
          throw new Error('Not Authorized');
        }
        setAuthenticatedTraveler(traveler);
        emitLoginForRequest(loginRequest);

        if (requiresPhoneConfirmation) {
          const params = new URLSearchParams(window.location.search);
          navigate(buildLoginPath('confirm_phone', params));
          return;
        }

        setIsAuthenticated(true);
      } catch (e) {
        // Local clear only — logout() would delete the bearer token on the server,
        // which can wipe a concurrent successful login and leave hard-refresh with
        // nothing to restore.
        clearAuthenticatedTraveler();
        setIsAuthenticated(false);
        throw (e instanceof Error ? e : new Error(String(e)));
      } finally {
        loginInFlightRef.current = null;
      }
    })();

    loginInFlightRef.current = attempt;
    return attempt;
  };

  return { login };
};

export default useLoginAuth;
